<template>
  <div>
    <card-dialog
      v-model="modalVisible"
      max-width="650px"
      :title="injuryFormData.id ? $t('edit_injury.dialog.header') : $t('new_injury.selected_player.dialog.header')"
    >
      <v-card-text
        ref="scollArea"
        class="pa-6 droparea"
        @dragenter.prevent="dragenter"
      >
        <validation-observer ref="validation">
          <div
            v-if="player.selected"
            class="rounded-sm grey-bg pa-2 mb-6 text-body-1"
          >
            <v-row
              no-gutters
              align="center"
            >
              <v-col
                cols="3"
                class="px-2 font-weight-medium"
              >
                {{ $t("player.label.title") }}
              </v-col>
              <v-col>
                {{ player.selected.first_name }} {{ player.selected.last_name }}
              </v-col>
              <v-col
                v-if="!injuryFormData.id"
                cols="auto"
                class="px-4"
              >
                <v-btn
                  small
                  text
                  class="text-none"
                  @click="player.selectDialog = true; modalVisible = false;"
                >
                  <v-icon
                    x-small
                    left
                  >
                    fal fa-pen
                  </v-icon>
                  {{ $t('button.change') }}
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <subtitle class="mb-3">
            {{ $t('selection.injured_area') }}
          </subtitle>

          <v-row v-if="player.selected">
            <v-col class="justify-center d-flex">
              <human-body-female-front-muscles
                v-if="player.selected.gender === 'female'"
                style="height: 400px; width: 220px"
                :stats="painStats"
                interactive
                @click="muscleToggle"
              />
              <human-body-front-muscles
                v-else
                style="height: 400px; width: 220px"
                :stats="painStats"
                interactive
                @click="muscleToggle"
              />
            </v-col>
            <v-col class="justify-center d-flex">
              <human-body-female-back-muscles
                v-if="player.selected.gender === 'female'"
                style="height: 400px; width: 220px"
                :stats="painStats"
                interactive
                @click="muscleToggle"
              />
              <human-body-back-muscles
                v-else
                style="height: 400px; width: 220px"
                :stats="painStats"
                interactive
                @click="muscleToggle"
              />
            </v-col>
          </v-row>

          <subtitle
            v-if="availableInjuryBodyParts.length > 0"
            class="mt-8"
          >
            {{ $t('selection.anatomy_structure') }}
          </subtitle>

          <v-list
            dense
            class="v-list--bordered mt-2 py-0 mb-8"
          >
            <v-list-item
              v-for="bodyPart in availableInjuryBodyParts"
              :key="bodyPart.id"
              :class="{ 'v-list-item--active': injuryFormData.body_part_ids.indexOf(bodyPart.id) !== -1 }"
              @click="injuryBodyPartToggle(bodyPart.id)"
            >
              <v-list-item-content>
                <v-list-item-title :class="{ 'primary--text': injuryFormData.body_part_ids.indexOf(bodyPart.id) !== -1 }">
                  {{ bodyPart.name }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon
                  v-if="injuryFormData.body_part_ids.indexOf(bodyPart.id) !== -1"
                  small
                  color="primary"
                >
                  fa fa-check
                </v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <validation-provider
            v-slot="{ errors }"
            key="injuryFormBodyParts"
            rules="required"
          >
            <input
              type="hidden"
              :value="injuryFormData.body_part_ids.length > 0 ? true : undefined"
            >
            <div
              v-if="errors.length > 0"
              class="error--text mt-2 mb-8 text-body-1"
            >
              {{ $t('alert.select_anatomy') }}
            </div>
          </validation-provider>

          <subtitle class="mb-3">
            {{ $t('picker.injury_occurrence.title') }}
          </subtitle>

          <validation-provider
            v-slot="{ errors }"
            key="injuryFormDate"
            rules="required"
          >
            <v-menu
              v-model="dateSelectorActive"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="injuryFormData.date ? $dayjs(injuryFormData.date).format(getDateTimeFormatForUser('D MMM, YYYY')) : ''"
                  :label="$t('picker.date.placeholder')"
                  outlined
                  hide-details
                  :error-messages="errors"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="injuryFormData.date"
                :first-day-of-week="1"
                no-title
                :max="injuryDateMax && $dayjs(injuryDateMax).isBefore($dayjs()) ? injuryDateMax : $dayjs().format('YYYY-MM-DD')"
                @change="dateSelectorActive = false"
              />
            </v-menu>
          </validation-provider>

          <v-switch
            v-if="!injuryFormData.id && injuryFormData.date && $dayjs().diff($dayjs(injuryFormData.date), 'day') > 5"
            v-model="injuryFormData.still_active"
            :label="$t('injury_still_active.label')"
            hide-details
          />

          <div
            v-if="events.data.length > 0"
            class="mt-8"
          >
            <subtitle class="mb-3">
              {{ $t('injury.event_suggestion') }}
            </subtitle>

            <v-list
              dense
              class="v-list--bordered py-0 mb-8"
            >
              <v-list-item
                v-for="event in events.data"
                :key="event.id"
                :class="{ 'v-list-item--active': events.selectedEvent && events.selectedEvent.id === event.id }"
                @click="events.selectedEvent && events.selectedEvent.id === event.id ? events.selectedEvent = undefined : events.selectedEvent = event"
              >
                <v-list-item-content>
                  <v-list-item-title :class="{ 'primary--text': events.selectedEvent && events.selectedEvent.id === event.id }">
                    {{ event.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="mt-1">
                    <v-chip
                      class="mr-2"
                      label
                      :color="eventTypes[event.event_type].color"
                      dark
                      x-small
                    >
                      {{ eventTypes[event.event_type].text }}
                    </v-chip>
                    {{ $dayjs(event.start_at).format(getDateTimeFormatForUser('ddd, D MMMM, HH:mm')) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="events.selectedEvent && events.selectedEvent.id === event.id">
                  <v-icon
                    small
                    color="grey"
                  >
                    fa fa-check
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>

          <div v-if="!injuryFormData.still_active">
            <subtitle class="mb-3 mt-8">
              {{ $t('dropdown.injury.return_to_competition.title') }}
            </subtitle>

            <validation-provider
              v-slot="{ errors }"
              key="injuryFormCompletedDate"
              rules="required"
            >
              <v-menu
                v-model="completedDateSelectorActive"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="injuryFormData.completed_date ? $dayjs(injuryFormData.completed_date).format(getDateTimeFormatForUser('D MMM, YYYY')) : ''"
                    :label="$t('picker.date.placeholder')"
                    outlined
                    hide-details
                    :error-messages="errors"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="injuryFormData.completed_date"
                  :first-day-of-week="1"
                  no-title
                  :max="$dayjs().format('YYYY-MM-DD')"
                  :min="completedDateSelectorMin"
                  @change="completedDateSelectorActive = false"
                />
              </v-menu>
            </validation-provider>
          </div>

          <subtitle class="mb-3 mt-8">
            {{ $t('dropdown.injury.provide_more.title') }}
          </subtitle>

          <validation-provider
            v-slot="{ errors }"
            key="injuryFormTypes"
            rules="required"
          >
            <v-autocomplete
              v-model="injuryFormData.injury_type_ids"
              :label="$t('dropdown.injuries.title')"
              outlined
              multiple
              :items="injuryTypes.data"
              item-value="id"
              item-text="type"
              :error-messages="errors"
            />
          </validation-provider>

          <v-select
            v-model="injuryFormData.character"
            :label="$t('dropdown.character.title')"
            outlined
            item-value="key"
            item-text="text"
            :items="characterList"
          />

          <v-select
            v-model="injuryFormData.contact"
            :label="$t('dropdown.contact.title')"
            outlined
            item-value="key"
            item-text="text"
            :items="contactList"
          />

          <v-select
            v-model="injuryFormData.mechanism"
            :label="$t('dropdown.mechanism.title')"
            outlined
            item-value="key"
            item-text="text"
            :items="mechanismList"
          />

          <v-select
            v-model="injuryFormData.injury_surface_id"
            :label="$t('injury.surface.label')"
            outlined
            item-value="id"
            item-text="surface"
            :loading="injurySurfaces.loading"
            :items="injurySurfaces.data"
          />

          <subtitle class="mb-3 mt-2">
            {{ $t('text_input.provide_short_title.title') }}
          </subtitle>

          <validation-provider
            v-slot="{ errors }"
            key="injuryFormTitle"
            rules="required"
          >
            <v-text-field
              v-model="injuryFormData.title"
              outlined
              :label="$t('text_input.title.title')"
              :error-messages="errors"
            />
          </validation-provider>

          <div v-if="!injuryFormData.id">
            <subtitle class="mb-3 mt-3">
              {{ $t('text_input.add_notes.title') }}
            </subtitle>
            <div>
              <rich-text-editor v-model="injuryFormData.note" />
            </div>

            <v-overlay
              color="white"
              light
              :value="fileUpload.isDragging"
              :opacity="0.8"
              @dragleave.prevent="dragleave"
              @dragover.prevent="dragover"
              @drop.prevent="drop"
            >
              <div
                class="mb-4 text-center"
                style="pointer-events: none"
              >
                <v-icon
                  x-large
                  color="grey darken-4"
                >
                  fal fa-upload
                </v-icon>
              </div>
              <div
                class="grey--text text--darken-4"
                style="pointer-events: none"
              >
                {{ $t('card_title.release_to_upload') }}
              </div>
            </v-overlay>

            <v-row
              align="center"
              class="mb-4 mt-2"
            >
              <v-col class="text-body-2 grey--text">
                {{ $t('media_upload.instruction') }}
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="grey darken-2"
                  text
                  class="text-none"
                  @click="$refs.uploadFile.click()"
                >
                  {{ $t('button.browse') }}
                  <v-icon
                    right
                    small
                  >
                    fal fa-upload
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <input
              id="upload-file"
              ref="uploadFile"
              type="file"
              style="display: none"
              accept="video/*,audio/*,image/*,.doc,.docx,.pptx,.xlsx,.xls,application/pdf,application/octet-stream,.dcm"
              @input="handleFileChange"
            >

            <div>
              <v-row>
                <v-col
                  v-for="(mediaItem, mediaItemIndex) in media"
                  :key="mediaItem.id"
                  cols="6"
                >
                  <media-grid-item
                    :media-item="mediaItem"
                    @preview="preview"
                    @delete="mediaItem.id ? startMediaDelete(mediaItem) : media.splice(mediaItemIndex, 1)"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
        </validation-observer>
      </v-card-text>
      <template v-slot:actions>
        <v-card-actions class="pa-4">
          <div class="flex-grow-1">
            <v-btn
              color="primary"
              block
              large
              :loading="submitting"
              :disabled="media.some(m => m.upload)"
              @click="submit()"
            >
              {{ $t('training_builder.button.save') }}
            </v-btn>
            <v-btn
              v-if="injuryFormData.id"
              color="error"
              block
              text
              class="mt-2"
              :loading="deleting"
              @click="deleteConfirmationDialogVisible = true"
            >
              {{ $t('button.delete') }}
            </v-btn>
          </div>
        </v-card-actions>
      </template>

      <media-viewer-dialog
        :visible.sync="mediaViewerDialog.visible"
        :media="media"
        :view-media-id="mediaViewerDialog.media ? mediaViewerDialog.media.id : undefined"
        @media:updated="updateMedia"
        @media:deleted="removeMedia"
      />

      <delete-confirmation-dialog
        :visible.sync="deleteMedia.dialog"
        :submitting="deleteMedia.deleting"
        @confirmed="destroyMedia()"
      />
    </card-dialog>

    <card-dialog
      v-model="player.selectDialog"
      :title="$t('select_player.dialog.header')"
      max-width="500px"
    >
      <v-card-text class="pa-6">
        <v-text-field
          v-model="player.search"
          dense
          outlined
          append-icon="fal fa-search"
          :placeholder="$t('text_input.search_players.title')"
        />
        <v-list class="v-list--bordered pa-0 ma-0">
          <v-list-item
            v-for="p in filteredPlayerItems"
            :key="p.id"
            @click="selectPlayer(p)"
          >
            <v-list-item-avatar>
              <v-avatar
                size="32"
                class="mr-2"
                color="grey lighten-2"
              >
                <v-img :src="p.photo_url">
                  <template v-slot:placeholder>
                    <v-icon x-small>
                      fal fa-user
                    </v-icon>
                  </template>
                </v-img>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-title>
              {{ p.first_name }} {{ p.last_name }}
            </v-list-item-title>
            <v-list-item-action>
              <v-icon>
                fal fa-arrow-right
              </v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card-text>
    </card-dialog>
  </div>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { EventBus } from '@/plugins/bus'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import { InjuryApiService } from '@/data/services/InjuryApiService'
import _axios from '@/plugins/axios'
import MediaGridItem from '@/components/helpers/MediaGridItem.vue'
import MediaViewerDialog from '@/components/dialogs/MediaViewerDialog.vue'
import { MediaApiService } from '@/data/services/MediaApiService'
import { EventApiService, PlayerApiService } from '@/data/services'
import HumanBodyFemaleFrontMuscles from '@/components/human-body/HumanBodyFemaleFrontMuscles.vue'
import HumanBodyBackMuscles from '@/components/human-body/HumanBodyBackMuscles.vue'
import HumanBodyFemaleBackMuscles from '@/components/human-body/HumanBodyFemaleBackMuscles.vue'
import HumanBodyFrontMuscles from '@/components/human-body/HumanBodyFrontMuscles.vue'
import Subtitle from '@/components/helpers/Subtitle.vue'
import * as Player from '@/data/models'
import { EventType, EventTypes } from '@/data/models'
import dayjs from 'dayjs'
import RichTextEditor from '@/components/inputs/RichTextEditor.vue'
import { CharacterList, ContactList, InjuryPhase, MechanismList } from '@/data/models/Injury'
import { cloneDeep, sortBy } from 'lodash'
import { getDateTimeFormatForUser, toCamelCase } from '@/utils/helpers'

const emptyInjuryFormData = {
  id: undefined,
  title: '',
  body_part_ids: [],
  date: null,
  completed_date: null,
  event_id: null,
  injury_type_ids: [],
  recurrent: false,
  character: undefined,
  mechanism: undefined,
  injury_surface_id: undefined,
  contact: null,
  still_active: true,
  note: ''
}

export default {
  name: 'InjuryFormDialog',
  components: {
    RichTextEditor,
    Subtitle,
    HumanBodyFrontMuscles,
    HumanBodyFemaleBackMuscles,
    HumanBodyBackMuscles,
    HumanBodyFemaleFrontMuscles,
    MediaViewerDialog,
    MediaGridItem,
    DeleteConfirmationDialog,
    CardDialog
  },
  mixins: [deleteConfirmationMixin],
  props: {
    injury: {
      type: Object,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      player: {
        teamId: 'all',
        search: '',
        selectDialog: false,
        items: [],
        itemsLoading: false,
        selected: undefined
      },
      injuryFormData: { ...emptyInjuryFormData },
      humanBodySelected: [],
      dateSelectorActive: false,
      completedDateSelectorActive: false,
      injuryDateMax: undefined,
      injuryPhaseReturnTrainingDate: undefined,
      events: {
        loading: false,
        data: [],
        selectedEvent: undefined
      },
      injuryTypes: {
        loading: false,
        data: []
      },
      injuryBodyParts: {
        loading: false,
        data: []
      },
      injurySurfaces: {
        loading: false,
        data: []
      },
      submitting: false,
      groups: {
        loading: false,
        data: []
      },
      fileUpload: {
        isDragging: false,
        isUploading: false
      },
      media: [],
      mediaViewerDialog: {
        visible: false,
        media: undefined
      },
      deleteMedia: {
        media: undefined,
        dialog: false,
        deleting: false
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    filteredPlayerItems() {
      if (!this.player.search) return this.player.items

      return this.player.items.filter(p => {
        return (p.first_name + ' ' + p.last_name).toLowerCase().indexOf(this.player.search.toLowerCase()) > -1
      })
    },
    painStats() {
      const object = {}

      for (const muscle of this.humanBodySelected) {
        object[muscle] = 3
      }

      return object
    },
    availableInjuryBodyParts() {
      return this.injuryBodyParts.data.filter(part => {
        return this.humanBodySelected.indexOf(part.main_body_part) !== -1
      })
    },
    completedDateSelectorMin() {
      let latestDate

      if (this.injuryFormData.date) {
        latestDate = this.injuryFormData.date
      }

      if (this.injuryPhaseReturnTrainingDate && (!latestDate || this.$dayjs(this.injuryPhaseReturnTrainingDate).isAfter(this.$dayjs(latestDate)))) {
        latestDate = this.injuryPhaseReturnTrainingDate
      }

      return latestDate
    }
  },
  watch: {
    'injuryFormData.date': function () {
      if (!this.player.selectDialog) {
        this.fetchEvents()
      }
    }
  },
  created() {
    this.musclesAndJoints = Player.musclesAndJoints
    this.eventTypes = EventTypes
    this.characterList = Object.keys(CharacterList).map(k => ({ key: k, ...CharacterList[k] }))
    this.contactList = [{ key: null, text: this.$t('contact.no_contact') }, ...Object.keys(ContactList).map(k => ({ key: k, ...ContactList[k] }))]
    this.mechanismList = Object.keys(MechanismList).map(k => ({ key: k, ...MechanismList[k] }))

    EventBus.$on(this.$events.OPEN_DIALOG_INJURY_FORM, () => {
      this.player.selectDialog = true
      this.injuryFormData = cloneDeep(emptyInjuryFormData)
      this.injuryFormData.date = this.$dayjs().format('YYYY-MM-DD')
      this.events.selectedEvent = undefined
      this.media = []
      this.humanBodySelected = []
      this.injuryDateMax = undefined
      this.injuryPhaseReturnTrainingDate = undefined

      this.fetchAllPlayers()
      this.fetchInjuryTypes()
      this.fetchInjuryBodyParts()
      this.fetchInjurySurfaces()
    })

    EventBus.$on(this.$events.EDIT_INJURY, (injury) => {
      this.humanBodySelected = []

      this.player.selected = injury.player

      this.injuryFormData = cloneDeep(emptyInjuryFormData)
      this.injuryFormData.id = injury.id
      this.injuryFormData.title = injury.title
      this.injuryFormData.body_part_ids = injury.injury_body_parts.map(bp => bp.id)
      this.injuryFormData.date = this.$dayjs(injury.date).format('YYYY-MM-DD')

      if (injury.completed_date) {
        this.injuryFormData.completed_date = this.$dayjs(injury.completed_date).format('YYYY-MM-DD')
        this.injuryFormData.still_active = false
      } else {
        this.injuryFormData.still_active = true
      }

      this.injuryFormData.event_id = injury.event_id
      this.injuryFormData.injury_type_ids = injury.injury_types.map(it => it.id)
      this.injuryFormData.recurrent = injury.recurrent
      this.injuryFormData.character = injury.character
      this.injuryFormData.mechanism = injury.mechanism
      this.injuryFormData.injury_surface_id = injury.injury_surface_id
      this.injuryFormData.contact = injury.contact

      for (const bodyPart of injury.injury_body_parts) {
        if (this.humanBodySelected.indexOf(bodyPart.main_body_part) === -1) this.humanBodySelected.push(bodyPart.main_body_part)
      }

      const injuryPhaseDiagnosis = injury.injury_phases.find(injuryPhase => injuryPhase.phase === InjuryPhase.DIAGNOSIS)
      const injuryPhaseLatest = injury.injury_phases[injury.injury_phases.length - 1]

      this.injuryDateMax = injuryPhaseDiagnosis ? this.$dayjs(injuryPhaseDiagnosis.date).format('YYYY-MM-DD') : undefined
      this.injuryPhaseReturnTrainingDate = injuryPhaseLatest ? this.$dayjs(injuryPhaseLatest.date).format('YYYY-MM-DD') : undefined

      this.modalVisible = true

      this.$nextTick(() => {
        if (injury.event_id) {
          this.events.selectedEvent = this.events.data.find(e => e.id === injury.event_id)
        }
      })

      this.fetchAllPlayers()
      this.fetchInjuryTypes()
      this.fetchInjuryBodyParts()
      this.fetchInjurySurfaces()
    })

    EventBus.$on(this.$events.PLAYER_CREATED, () => {
      this.fetchAllPlayers()
    })

    EventBus.$on(this.$events.PLAYER_UPDATED, () => {
      this.fetchAllPlayers()
    })

    EventBus.$on(this.$events.PLAYER_DELETED, () => {
      this.fetchAllPlayers()
    })
  },
  methods: {
    getDateTimeFormatForUser,
    async fetchAllPlayers() {
      try {
        this.player.itemsLoading = true

        const result = await PlayerApiService.getSimpleList({ order_by: 'last_name', order: 'ASC' })

        this.player.items = result.data
      } finally {
        this.player.itemsLoading = false
      }
    },
    selectPlayer(p) {
      this.player.selected = p
      this.player.selectDialog = false
      this.modalVisible = true

      this.$nextTick(() => {
        this.$refs.validation.reset()
        this.fetchEvents()
      })
    },
    muscleToggle(muscle) {
      const index = this.humanBodySelected.indexOf(muscle)

      if (index > -1) {
        this.humanBodySelected.splice(index, 1)

        this.injuryFormData.body_part_ids = this.injuryFormData.body_part_ids.filter(bpid => {
          return this.availableInjuryBodyParts.some(part => part.id === bpid)
        })
      } else {
        this.humanBodySelected.push(muscle)
      }
    },
    injuryBodyPartToggle(bodyPartId) {
      const index = this.injuryFormData.body_part_ids.indexOf(bodyPartId)

      if (index > -1) {
        this.injuryFormData.body_part_ids.splice(index, 1)
      } else {
        this.injuryFormData.body_part_ids.push(bodyPartId)
      }
    },
    async fetchEvents() {
      this.events.loading = true

      try {
        const response = await EventApiService.getAll({
          from: dayjs(this.injuryFormData.date).format('YYYY-MM-DD'),
          to: dayjs(this.injuryFormData.date).format('YYYY-MM-DD'),
          player_id: this.player.selected.id || undefined,
          event_types: [EventType.TRAINING, EventType.MATCH],
          compact: true
        })

        this.events.data = response.data
      } catch (e) {

      } finally {
        this.events.loading = false
      }
    },
    async fetchInjuryTypes() {
      this.injuryTypes.loading = true

      try {
        const response = await InjuryApiService.fetchInjuryTypes({
          order: 'injury_type'
        })

        this.injuryTypes.data = sortBy(response.data.map((injury) => {
            return {
              id: injury.id,
              type: this.$t(`injuryType.${toCamelCase(injury.type)}`)
            }
          }),
          'type'
        )
      } finally {
        this.injuryTypes.loading = false
      }
    },
    async fetchInjuryBodyParts() {
      this.injuryBodyParts.loading = true

      try {
        const response = await InjuryApiService.fetchInjuryBodyParts({})

        this.injuryBodyParts.data = sortBy(response.data, 'name')
      } finally {
        this.injuryBodyParts.loading = false
      }
    },
    async fetchInjurySurfaces() {
      this.injurySurfaces.loading = true

      try {
        const response = await InjuryApiService.fetchInjurySurfaces({})

        this.injurySurfaces.data = response.data.map((surface) => {
          return {
            id: surface.id,
            surface: this.$t(`surface.${surface.slug}`),
            slug: surface.slug
          }
        })
      } finally {
        this.injurySurfaces.loading = false
      }
    },
    async submit() {
      if (!(await this.$refs.validation.validate())) {
        this.$vuetify.goTo('.error--text', { container: this.$refs.scollArea, offset: 400 })

        return
      }

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        let data
        let result

        if (!this.injuryFormData.id) {
          data = {
            player_id: this.player.selected.id,
            title: this.injuryFormData.title,
            body_part_ids: this.injuryFormData.body_part_ids,
            event_id: this.events.selectedEvent && this.events.data.some(e => e.id === this.events.selectedEvent.id) ? this.events?.selectedEvent?.id : null,
            date: this.injuryFormData.date,
            completed_date: this.injuryFormData.still_active ? null : this.injuryFormData.completed_date,
            injury_type_ids: this.injuryFormData.injury_type_ids,
            recurrent: this.injuryFormData.recurrent,
            character: this.injuryFormData.character,
            mechanism: this.injuryFormData.mechanism,
            injury_surface_id: this.injuryFormData.injury_surface_id,
            contact: this.injuryFormData.contact,
            note: this.injuryFormData.note,
            media_ids: this.media.map(m => m.id),
            still_active: this.injuryFormData.still_active
          }

          result = await InjuryApiService.createInjury(data)
        } else {
          data = {
            title: this.injuryFormData.title,
            body_part_ids: this.injuryFormData.body_part_ids,
            event_id: this.events.selectedEvent && this.events.data.some(e => e.id === this.events.selectedEvent?.id) ? this.events?.selectedEvent?.id : null,
            date: this.injuryFormData.date,
            completed_date: this.injuryFormData.still_active ? null : this.injuryFormData.completed_date,
            injury_type_ids: this.injuryFormData.injury_type_ids,
            recurrent: this.injuryFormData.recurrent,
            character: this.injuryFormData.character,
            mechanism: this.injuryFormData.mechanism,
            injury_surface_id: this.injuryFormData.injury_surface_id,
            contact: this.injuryFormData.contact,
            note: this.injuryFormData.note,
            media_ids: this.media.map(m => m.id)
          }

          result = await InjuryApiService.updateInjury(this.injuryFormData.id, data)
        }

        this.$emit('refresh', result.data)

        if (this.injuryFormData.id) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.injury_update.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.INJURY_UPDATED, result.data)
        } else {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.injury_save.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.INJURY_CREATED, result.data)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.exercise_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async handleFileChange() {
      await this.$nextTick()

      const files = [...this.$refs.uploadFile.files]

      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'dkg', 'dcm']

      for (const file of files) {
        const type = file.type
        const extension = file.name.split('.').pop()

        if (type.match('image.*') || type.match('video.*') || type.match('audio.*') || allowedExtensions.indexOf(extension) > -1) {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            upload: true
          })
        } else {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            error: 'File type not allowed'
          })
        }
      }

      this.$refs.uploadFile.value = null

      this.processUpload()
    },
    preview(media) {
      this.mediaViewerDialog.visible = true
      this.mediaViewerDialog.media = media
    },
    dragover(e) {
      this.fileUpload.isDragging = true
    },
    dragleave(e) {
      this.fileUpload.isDragging = false
    },
    dragenter(e) {
      this.fileUpload.isDragging = true
    },
    drop(e) {
      this.$refs.uploadFile.files = e.dataTransfer.files
      this.handleFileChange()
      this.fileUpload.isDragging = false
    },
    startMediaDelete(media) {
      this.deleteMedia.media = media
      this.deleteMedia.dialog = true
    },
    async processUpload() {
      let formData

      for (const file of this.media.filter(m => !!m.upload)) {
        formData = new FormData()
        formData.append('file', file.file, file.name)

        _axios.post('training-exercises/media-upload', formData, {
          onUploadProgress: (e) => {
            file.progress = Number((e.loaded / e.total) * 100)
          }
        }).then(response => {
          file.upload = false

          Object.assign(file, response.data.media)
        }).catch(error => {
          if (error.response && error.response.status === 422) {
            this.$store.dispatch(this.$storeActions.ALERT, {
              color: 'error',
              text: this.$t('alert.file_type_not_allowed.error')
            })

            file.error = this.$t('alert.file_type_not_allowed.error')

            return
          }

          this.$store.dispatch(this.$storeActions.ALERT, {
            color: 'error',
            text: this.$t('alert.uploading_file.error')
          })

          file.error = this.$t('alert.uploading_file.error')
        })
      }
    },
    async destroyMedia() {
      if (!this.deleteMedia.media) return

      const id = this.deleteMedia.media.id

      try {
        this.deleteMedia.deleting = true

        await MediaApiService.delete(id)

        const index = this.media.findIndex(m => m.id === id)

        if (index > -1) this.media.splice(index, 1)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_delete.success'),
          color: 'success'
        })

        this.deleteMedia.dialog = false

        EventBus.$emit(this.$events.INJURY_UPDATED, this.injuryFormData)
      } catch (e) {

      } finally {
        this.deleteMedia.media = undefined
        this.deleteMedia.deleting = false
      }
    },
    updateMedia(media) {
      const index = this.media.findIndex(m => m.id === media.id)

      if (index > -1) {
        this.media.splice(index, 1, media)
      }

      this.$emit('success')
    },
    removeMedia(media) {
      const index = this.media.findIndex(m => m.id === media.id)

      if (index > -1) {
        this.media.splice(index, 1)
      }

      this.$emit('success')
    }
  }
}
</script>
